/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import Paper from "@mui/material/Paper";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import { useForm } from "@/src/provider/Form/FormProvider";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import { requiredValidator } from "@/src/services/validationRules";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box } from "@mui/material";
import WaitingForAiResponse from "@/src/components/Form/Common/WaitingForAiResponse/WaitingForAiResponse";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { useWaitForAsyncFields } from "@/src/hooks/useWairForAsyncFields";

type ConcernSummaryProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function ConcernSummary({ submitProps, product }: ConcernSummaryProps) {
  const { handleSubmit } = useForm();

  const { isWaiting: isWaitingForAiResponse } = useWaitForAsyncFields<AbstractCase>({
    product,
    requiredFields: ["finalClientDescriptionAndObjectives"],
    timeoutMs: 30_000,
  });

  if (isWaitingForAiResponse) {
    return (
      <WaitingForAiResponse
        mobileHeadline={"Zusammenfassung"}
        headline={"Wir fassen Ihr Anliegen abschließend zusammen"}
        text={"Ihr Anliegen wird gerade abschließend zusammengefasst. Dies kann bis zu einer Minute dauern. Bitte laden Sie die Seite nicht neu."}
      />
    );
  }

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>Wir fassen Ihr Anliegen abschließend zusammen</strong>
              </FormLegend>
            </Grid>
            <Grid
              size={12}
              sx={[
                {
                  mb: 4,
                },
                wizardStyle.gridTopSpaced,
              ]}
            >
              <FormLegend>Bitte prüfen Sie, ob Ihr Anliegen korrekt aufgenommen wurde. Sie können gerne Informationen ergänzen oder anpassen.</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                name="finalClientDescriptionAndObjectives"
                label="Zusammenfassung des Anliegens"
                InputLabelProps={{
                  shrink: true,
                }}
                multiline
                rows={10}
                validators={[requiredValidator("Bitte ergänzen Sie die notwendigen Informationen.")]}
              />
            </Grid>
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
